import React, { useEffect, useState } from "react";
import { Check, Loader2, Coins } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import PaymentBadges from "../components/PaymentBadges";

export default function Packages() {
  const { user, refreshUser } = useAuth();
  const [packages, setPackages] = useState([]);
  const [purchasing, setPurchasing] = useState(null);

  useEffect(() => {
    api.get("/packages").then(({ data }) => setPackages(data));
  }, []);

  const handlePurchase = async (pkgId) => {
    setPurchasing(pkgId);
    try {
      const { data } = await api.post("/packages/purchase", { package_id: pkgId });
      await refreshUser();
      toast.success(`${data.package.credits} kredi hesabınıza eklendi!`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Satın alma başarısız");
    } finally {
      setPurchasing(null);
    }
  };

  return (
    <div className="p-6 md:p-12 max-w-6xl mx-auto">
      <div className="mb-10">
        <div className="text-xs uppercase tracking-[0.3em] text-[#C29B62] mb-2">Paketler</div>
        <h1 className="font-display text-4xl font-bold tracking-tight">Kredi Satın Al</h1>
        <p className="text-slate-400 mt-3">Videolarınız için önceden kredi alın ve avantajlı fiyatlardan yararlanın.</p>
      </div>

      <div className="flex items-center gap-3 p-4 rounded-lg bg-[#C29B62]/5 border border-[#C29B62]/30 mb-10">
        <Coins size={20} className="text-[#C29B62]" />
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-slate-400">Mevcut Kredim</div>
          <div data-testid="current-credits" className="font-display text-2xl font-bold text-[#C29B62]">{user?.credits ?? 0}</div>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {packages.map(pkg => (
          <div key={pkg.package_id} data-testid={`package-${pkg.package_id}`}
            className={`relative rounded-xl p-8 bg-slate-900/40 backdrop-blur-xl transition hover:-translate-y-1 duration-300
              ${pkg.popular ? "tracing-border border-[#C29B62]/40" : "border border-white/10"}`}>
            {pkg.popular && (
              <div className="absolute -top-3 left-8 px-3 py-1 rounded-full bg-[#C29B62] text-slate-950 text-[10px] uppercase tracking-[0.25em] font-bold">
                Popüler
              </div>
            )}
            <div className="relative z-10">
              <div className="text-xs uppercase tracking-[0.25em] text-slate-400">{pkg.name}</div>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="font-display text-5xl font-black">{pkg.price_try}</span>
                <span className="text-xl text-slate-400">₺</span>
              </div>
              <div className="text-sm text-slate-400 mt-2">{pkg.credits} video kredisi</div>
              <div className="text-xs text-[#C29B62] mt-1">
                Video başı {Math.round(pkg.price_try / pkg.credits)}₺
              </div>
              <p className="text-sm text-slate-300 mt-6">{pkg.description}</p>
              <ul className="mt-6 space-y-3">
                <li className="flex gap-3 items-start text-sm text-slate-200">
                  <Check size={16} className="text-[#C29B62] mt-0.5 shrink-0" /> 60 saniyelik sinematik video
                </li>
                <li className="flex gap-3 items-start text-sm text-slate-200">
                  <Check size={16} className="text-[#C29B62] mt-0.5 shrink-0" /> Logo filigranı
                </li>
                <li className="flex gap-3 items-start text-sm text-slate-200">
                  <Check size={16} className="text-[#C29B62] mt-0.5 shrink-0" /> Stok müzik kütüphanesi
                </li>
              </ul>
              <button data-testid={`buy-${pkg.package_id}`} disabled={purchasing === pkg.package_id}
                onClick={() => handlePurchase(pkg.package_id)}
                className={`mt-8 w-full py-3 rounded-md font-semibold transition disabled:opacity-50 inline-flex items-center justify-center gap-2
                  ${pkg.popular ? "bg-[#C29B62] text-slate-950 hover:bg-[#D4AF7A]" : "border border-white/15 hover:border-[#C29B62]/60 hover:bg-white/5"}`}>
                {purchasing === pkg.package_id && <Loader2 size={16} className="animate-spin" />}
                Satın Al
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 p-4 rounded-lg bg-amber-500/5 border border-amber-500/20 text-xs text-amber-200/80 uppercase tracking-[0.2em]">
        Demo modunda gerçek ödeme alınmaz. Satın alma anında krediler hesabınıza yansır.
      </div>

      <div className="mt-10 pt-8 border-t border-white/5 grid gap-6 md:grid-cols-2">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-slate-400 mb-3">Güvenli Ödeme</div>
          <PaymentBadges data-testid="packages-payment-badges" />
          <p className="text-xs text-slate-500 mt-3 leading-relaxed">
            Tüm ödemeler <strong className="text-slate-300">iyzico</strong> güvencesiyle 256-bit SSL
            şifreleme üzerinden işlenir. Kart bilgileri sunucularımızda saklanmaz.
          </p>
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-slate-400 mb-3">Yasal</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/iptal-iade" className="text-slate-300 hover:text-[#C29B62]">İptal ve İade Koşulları</Link></li>
            <li><Link to="/teslimat" className="text-slate-300 hover:text-[#C29B62]">Teslimat ve Hizmet Sunumu</Link></li>
            <li><Link to="/kvkk" className="text-slate-300 hover:text-[#C29B62]">KVKK Aydınlatma Metni</Link></li>
            <li><Link to="/cerez-politikasi" className="text-slate-300 hover:text-[#C29B62]">Çerez Politikası</Link></li>
          </ul>
        </div>
      </div>
    </div>
  );
}
